/**
 * Posse, ordenação e deduplicação dos módulos de receita.
 *
 * Esta regra morava inteira dentro de TodosContenidos.tsx. A home passou a
 * responder "o que eu comprei?" com os mesmos dados, e duas cópias da regra
 * de posse são duas chances de a home mostrar um produto que a outra tela
 * esconde — ou o contrário, que é pior: a pessoa vê a capa na home e bate
 * num paywall ao tocar.
 *
 * Nada de regra mudou na mudança de arquivo: mesmas queries, mesmas chaves
 * de cache (`recipe-modules-todos` e `recipe-counts-by-module`, então as duas
 * telas compartilham o mesmo cache do react-query e a segunda não refaz
 * requisição), mesma ordem, mesmo dedupe, mesmos ids.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { getProductImageUrl } from "@/hooks/useCatalog";
import { supabase } from "@/integrations/supabase/client";
import type { UnifiedContentItem } from "@/lib/contentPriority";
import { MAIN_PRODUCT_GROUPS } from "@/lib/mainProductIds";

/** GlucoPhoto não é coleção de receitas: não entra na estante nem no upsell. */
export const GLUCOPHOTO_IDS = ["7330458", "7468894", "7487694"];

/**
 * Se a pessoa tem QUALQUER variante da mesma família de MAIN_PRODUCT_GROUPS,
 * o módulo conta como comprado — comprar Clean Sweets EN (7381203) impede
 * Repostería Cero ES (7368845) de aparecer como "experimentar".
 */
export const ownsModuleOrVariant = (
  pid: string | null,
  effectiveProducts: string[],
): boolean => {
  if (!pid) return false;
  if (effectiveProducts.includes(pid)) return true;
  const group = MAIN_PRODUCT_GROUPS.find((g) => g.ids.includes(pid));
  return !!group && group.ids.some((id) => effectiveProducts.includes(id));
};

/**
 * Mapeia um id de produto para o nome da família em MAIN_PRODUCT_GROUPS (ou
 * para o próprio id, se ele não está em nenhum grupo). É o que impede as
 * variantes ES/EN/DE do mesmo produto de virarem dois cartões na mesma lista.
 */
export const getFamilyKey = (pid: string | null): string | null => {
  if (!pid) return null;
  const group = MAIN_PRODUCT_GROUPS.find((g) => g.ids.includes(pid));
  return group ? group.name : pid;
};

type RecipeModuleRow = {
  id: string;
  name: string;
  hotmart_product_id: string | null;
  image: string | null;
  checkout_url: string | null;
  language?: string | null;
  [key: string]: unknown;
};

/**
 * Separa os módulos em comprados e "experimentar", na ordem de exibição.
 * Cópia literal do bloco que estava no useMemo de TodosContenidos.
 */
export function selectOwnedModules({
  recipeModules,
  recipeCountsByModule,
  effectiveProducts,
  language,
  todos = false,
}: {
  recipeModules: RecipeModuleRow[] | undefined;
  recipeCountsByModule: Record<string, number> | undefined;
  effectiveProducts: string[];
  language: string;
  /**
   * Admin fora do modo teste: todo módulo ativo, comprado ou não — inclusive
   * um que ainda não tem produto na Hotmart (Clean Mornings antes do
   * lançamento). Sem isso o dono só chegava nele digitando a URL.
   */
  todos?: boolean;
}): { owned: UnifiedContentItem[] } {
  if (!recipeModules || !recipeCountsByModule) {
    return { owned: [] };
  }

  const allItems: UnifiedContentItem[] = [];

  // ── Recipe modules (core content) ──────────────────────────────
  const sortedModules = [...recipeModules].sort((a, b) => {
    const aPriority = MAIN_PRODUCT_GROUPS.findIndex(({ ids }) => ids.includes(a.hotmart_product_id || ""));
    const bPriority = MAIN_PRODUCT_GROUPS.findIndex(({ ids }) => ids.includes(b.hotmart_product_id || ""));
    const aNorm = aPriority === -1 ? 999 : aPriority;
    const bNorm = bPriority === -1 ? 999 : bPriority;
    if (aNorm !== bNorm) return aNorm - bNorm;
    const aL = a.language === language ? 0 : 1;
    const bL = b.language === language ? 0 : 1;
    if (aL !== bL) return aL - bL;
    const rcDiff = (recipeCountsByModule[b.id] || 0) - (recipeCountsByModule[a.id] || 0);
    if (rcDiff !== 0) return rcDiff;
    return (a.name || "").localeCompare(b.name || "", language);
  });

  const representedCoreProducts = new Set<string>();
  for (const mod of sortedModules) {
    if ((recipeCountsByModule[mod.id] || 0) === 0) continue;
    if (GLUCOPHOTO_IDS.includes(mod.hotmart_product_id || "")) continue;
    const dedupeKey = mod.hotmart_product_id || mod.id;
    if (representedCoreProducts.has(dedupeKey)) continue;
    representedCoreProducts.add(dedupeKey);
    allItems.push({
      id: `module-${mod.id}`,
      name: mod.name,
      hotmartProductId: mod.hotmart_product_id,
      type: "core",
      sourceId: mod.id,
      // "/ofertas/x.webp" é um arquivo do próprio app (public/); URL completa
      // fica como está; qualquer outra coisa é caminho no storage.
      image: mod.image
        ? mod.image.startsWith("http") || mod.image.startsWith("/") ? mod.image : getProductImageUrl(mod.image)
        : null,
      checkoutUrl: mod.checkout_url,
      moduleData: mod,
    });
  }

  // ── Final dedup pass by hotmart_product_id ──────────────────────
  // Safety net: even if upstream merges leak a duplicate (e.g. a recipe module
  // and a catalog product share the same hotmart_product_id), keep only the
  // first occurrence so the UI never shows duplicated cards.
  const dedupedAllItems: UnifiedContentItem[] = [];
  const seenIds = new Set<string>();
  const seenNames = new Set<string>();
  for (const item of allItems) {
    const pid = item.hotmartProductId;
    if (pid && seenIds.has(pid)) continue;
    // Only name-dedup items without a product ID — items with a pid are already
    // covered by seenIds, and name-deduping would incorrectly discard ES/EN variant
    // modules that share a translated name (e.g. "Panadería Cero" → "Clean Bakes").
    if (!pid && seenNames.has(item.name)) continue;
    if (pid) seenIds.add(pid);
    seenNames.add(item.name);
    dedupedAllItems.push(item);
  }

  // ── Só o que a pessoa comprou ───────────────────────────────────
  // Havia uma segunda lista aqui, `demo`: todo módulo com id de produto que a
  // pessoa NÃO tinha entrava nela e virava vitrine em "Probar". Somada à
  // amostra de 50 por módulo, quem não tinha comprado nada alcançava 559
  // receitas — 22% do catálogo. A vitrine saiu inteira.
  const ownedItems: UnifiedContentItem[] = [];

  // sortedModules already puts language-matching variants first, so the first
  // occurrence of each family in dedupedAllItems is always the preferred locale.
  const seenOwnedFamilies = new Set<string>();

  for (const item of dedupedAllItems) {
    if (!todos && !ownsModuleOrVariant(item.hotmartProductId, effectiveProducts)) continue;
    const fk = getFamilyKey(item.hotmartProductId);
    if (fk && seenOwnedFamilies.has(fk)) continue;
    if (fk) seenOwnedFamilies.add(fk);
    ownedItems.push(item);
  }

  return { owned: ownedItems };
}

/**
 * O que a home e "Mi Biblioteca" precisam saber: o que a pessoa comprou, o
 * que dá para experimentar, e quantas receitas tem cada módulo.
 */
export function useOwnedModules() {
  const { effectiveProducts, user, testerMode } = useAuth();
  const { language } = useLocale();
  const todos = !!user?.isAdmin && !testerMode;

  const { data: recipeModules } = useQuery({
    queryKey: ["recipe-modules-todos"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("recipe_modules").select("*").eq("active", true).order("sort_order");
      return data || [];
    },
  });

  /**
   * Quantas receitas cada módulo tem.
   *
   * A versão anterior baixava a tabela `recipes` inteira — uma coluna, mas
   * milhares de linhas, no celular da pessoa, só para escrever "193 recetas"
   * na primeira tela. E contava errado: o PostgREST corta a resposta no teto
   * de linhas do servidor, então os módulos cujas linhas ficavam além do corte
   * vinham com menos do que têm. Era daí que saía a home dizendo 193 e a tela
   * da coleção, que consulta só aquele módulo, dizendo 314 — o cliente via o
   * app se contradizer sem que nenhum dos dois números estivesse "errado" por
   * conta própria.
   *
   * Agora é uma contagem agregada por módulo: `head: true` não traz linha
   * nenhuma, só o total, e o total é o real.
   */
  const moduleIds = useMemo(
    () => ((recipeModules as RecipeModuleRow[] | undefined) || []).map((m) => m.id).filter(Boolean),
    [recipeModules],
  );

  const { data: recipeCountsByModule } = useQuery({
    queryKey: ["recipe-counts-by-module", moduleIds],
    enabled: moduleIds.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const pares = await Promise.all(
        moduleIds.map(async (id) => {
          const { count } = await supabase
            .from("recipes")
            .select("id", { count: "exact", head: true })
            .eq("module_id", id);
          return [id, count ?? 0] as const;
        }),
      );
      const counts: Record<string, number> = {};
      pares.forEach(([id, n]) => { counts[id] = n; });
      return counts;
    },
  });

  const { owned } = useMemo(
    () =>
      selectOwnedModules({
        recipeModules: recipeModules as RecipeModuleRow[] | undefined,
        recipeCountsByModule,
        effectiveProducts,
        language,
        todos,
      }),
    [recipeModules, recipeCountsByModule, effectiveProducts, language, todos],
  );

  return {
    owned,
    recipeCountsByModule,
    isLoading: !recipeModules || !recipeCountsByModule,
  };
}

/**
 * As bibliotecas de remédios (Clean Remedies em inglês, La Botica de la
 * Abuela em espanhol) não são módulos de `recipe_modules`: são páginas
 * internas do app, com produto próprio na Hotmart. A estante da home e a
 * biblioteca (/my-content) as mostram por aqui, com a mesma regra de posse.
 */
export const BIBLIOTECAS_DE_REMEDIOS: { productId: string; nome: string; rota: string; capa: string }[] = [
  { productId: "8073539", nome: "Clean Remedies", rota: "/clean-remedies", capa: "/ofertas/clean-remedies.webp" },
  { productId: "8131891", nome: "La Botica de la Abuela", rota: "/la-botica", capa: "/ofertas/la-botica.webp" },
];

export function bibliotecasDeRemediosDe(effectiveProducts: string[], isAdmin = false) {
  return BIBLIOTECAS_DE_REMEDIOS.filter((b) => isAdmin || effectiveProducts.includes(b.productId));
}
